import { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Brain,
  Search,
  Calendar,
  Star,
  BookOpen,
  Loader2,
  AlertTriangle,
  RefreshCw,
  Sparkles,
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { useMemoryStore, type MemoryEntry, type Skill } from '@/stores/memory'

const typeBadgeColors: Record<string, string> = {
  fact: 'bg-blue-600/20 text-blue-400 border-blue-500/30',
  preference: 'bg-purple-600/20 text-purple-400 border-purple-500/30',
  event: 'bg-emerald-600/20 text-emerald-400 border-emerald-500/30',
  insight: 'bg-amber-600/20 text-amber-400 border-amber-500/30',
  task: 'bg-sky-600/20 text-sky-400 border-sky-500/30',
  relationship: 'bg-pink-600/20 text-pink-400 border-pink-500/30',
}

function ImportanceStars({ level }: { level: number }) {
  const filled = Math.min(Math.max(Math.round(level / 2), 1), 5)
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={i}
          className={`h-3 w-3 ${i < filled ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground/30'}`}
        />
      ))}
    </div>
  )
}

function EntryCard({ entry }: { entry: MemoryEntry }) {
  return (
    <div className="flex items-start justify-between p-4 border border-border rounded-lg bg-muted/30 gap-4">
      <div className="flex-1 space-y-1.5 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className={typeBadgeColors[entry.type] || ''}>
            {entry.type}
          </Badge>
          <ImportanceStars level={entry.importance} />
        </div>
        <p className="text-sm text-foreground/90 break-words">{entry.content}</p>
        {entry.source && (
          <p className="text-xs text-muted-foreground">Source: {entry.source}</p>
        )}
      </div>
      <span className="text-xs text-muted-foreground shrink-0">
        {new Date(entry.createdAt).toLocaleDateString()}
      </span>
    </div>
  )
}

function SkillCard({ skill }: { skill: Skill }) {
  return (
    <div className="flex items-start gap-3 p-3 border border-border rounded-lg bg-muted/30">
      <Sparkles className="h-4 w-4 text-primary shrink-0 mt-0.5" />
      <div className="space-y-0.5 min-w-0">
        <p className="text-sm font-medium">{skill.name}</p>
        {skill.description && (
          <p className="text-xs text-muted-foreground break-words">{skill.description}</p>
        )}
      </div>
    </div>
  )
}

export default function MemoryPage() {
  const {
    memoryContent,
    entries,
    logContent,
    logDate,
    skills,
    isLoading,
    error,
    fetchMemory,
    fetchEntries,
    fetchLog,
    fetchSkills,
  } = useMemoryStore()

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedDate, setSelectedDate] = useState(
    () => new Date().toISOString().split('T')[0]
  )

  useEffect(() => {
    fetchMemory()
    fetchEntries()
    fetchLog()
    fetchSkills()
  }, [fetchMemory, fetchEntries, fetchLog, fetchSkills])

  const handleSearch = () => {
    fetchEntries(searchQuery || undefined)
  }

  const handleDateChange = (date: string) => {
    setSelectedDate(date)
    fetchLog(date)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Memory</h1>
          <p className="text-muted-foreground mt-1">
            Persistent memory, entries, daily logs, and skills
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            fetchMemory()
            fetchEntries(searchQuery || undefined)
            fetchLog(selectedDate)
            fetchSkills()
          }}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Refresh
        </Button>
      </div>

      {error && (
        <div className="flex items-start gap-3 p-4 rounded-lg border border-amber-500/30 bg-amber-500/5">
          <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-500">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        <div className="xl:col-span-3">
          <Tabs defaultValue="memory" className="space-y-4">
            <TabsList>
              <TabsTrigger value="memory">
                <Brain className="h-4 w-4 mr-2" />
                MEMORY.md
              </TabsTrigger>
              <TabsTrigger value="entries">
                <Search className="h-4 w-4 mr-2" />
                Entries
              </TabsTrigger>
              <TabsTrigger value="logs">
                <Calendar className="h-4 w-4 mr-2" />
                Daily Logs
              </TabsTrigger>
            </TabsList>

            <TabsContent value="memory">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Brain className="h-4 w-4" />
                    Persistent Memory
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {memoryContent ? (
                    <div className="prose prose-sm prose-invert max-w-none prose-headings:text-foreground prose-p:text-foreground/90 prose-a:text-primary prose-strong:text-foreground prose-li:text-foreground/90">
                      <ReactMarkdown>{memoryContent}</ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground py-8 text-center">
                      {isLoading ? 'Loading memory...' : 'No memory content found. The agent will populate this as it learns.'}
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="entries">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Memory Entries</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Search entries..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <Button size="sm" onClick={handleSearch}>
                      <Search className="h-4 w-4" />
                    </Button>
                  </div>
                  {entries.length > 0 ? (
                    <div className="space-y-3">
                      {entries.map((entry) => (
                        <EntryCard key={entry.id} entry={entry} />
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground py-8 text-center">
                      {isLoading ? 'Loading entries...' : 'No entries found.'}
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="logs">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-sm font-medium">Daily Log</CardTitle>
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => handleDateChange(e.target.value)}
                    className="w-auto"
                  />
                </CardHeader>
                <CardContent>
                  {logContent ? (
                    <div className="prose prose-sm prose-invert max-w-none prose-headings:text-foreground prose-p:text-foreground/90">
                      <ReactMarkdown>{logContent}</ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground py-8 text-center">
                      {isLoading ? 'Loading log...' : `No log for ${logDate || selectedDate}.`}
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Skills Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                Skills ({skills.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {skills.length > 0 ? (
                <div className="space-y-2">
                  {skills.map((skill) => (
                    <SkillCard key={skill.name} skill={skill} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No skills loaded.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
